// The Rhythm Lab's bass: the short, thuddy "baby bass" of a salsa band, and the anticipated bass line it plays, made by synthesis alone.
//
// A NOTE is five harmonic partials at f0 x 1..5, each a decaying sine with its own gain and decay time (the fundamental falls with a time constant of 0.30 s and
// harmonic k with 0.30 / k^0.7, so the upper harmonics die first). The upper harmonics matter: phone and laptop speakers cannot reproduce a fundamental near 65 to
// 100 Hz, so the note is heard through its 3rd to 5th partials. A PLUCK starts the note: a rise of 3 ms, a thump (6 ms of noise low-passed at 500 Hz, 10 dB below the
// note) and a faint finger click (noise band-passed 700 Hz to 1.5 kHz, 22 dB below). The sum is scaled to a fixed peak, soft-clipped with tanh (mild) and low-passed
// near 1.5 kHz, and the note is DAMPED: it is stopped before the next one starts, or after 0.40 s if that comes first, with a 30 ms release, so notes never overlap
// and never boom.
//
// The LINE is the anticipated bass: one chord per four counts, and the bass plays the FIFTH of the current chord on the "&" (2& and 6&) and the ROOT OF THE NEXT
// CHORD on the whole counts 4 and 8, a half-bar early. The progression is C, F, G, F (I-IV-V-IV) and takes two phrases (16 counts); a loop of an odd number of phrases
// cannot hold a whole cycle, so it plays the two-chord vamp C, G (one phrase) instead, and every loop joins without a wrong note: the last note of the loop is
// the root of the first chord. Every note is folded into C2 (65.4 Hz) to just under C3 (130.8 Hz).
//
// Every number is in `BASS` below, to be tuned by ear. All functions are pure and deterministic (the noise is a fixed hash, not Math.random).

import { PHRASE_BEATS } from './patterns.js';
import { butterworth, hashNoise } from './voicedsp.js';

/** A burst of noise at the start of a note. */
export interface BassNoise {
  /** Length of the burst in milliseconds; it fades out over its last quarter, so it ends without a click. */
  readonly ms: number;
  /** Pass band in Hz. A low corner of 0 leaves that side open (a low-pass). */
  readonly band: readonly [number, number];
  /** Fall of the burst, seconds (the amplitude is 1/e of its start after this long). */
  readonly tau: number;
  /** Level of the burst's RMS against the RMS of the note's partials at their start, in dB. */
  readonly db: number;
}

export interface BassSpec {
  /** For the interface. */
  readonly label: string;
  /** The partials, as multiples of the note's f0. */
  readonly harmonics: readonly number[];
  /** Level of each partial, relative to the fundamental. */
  readonly gains: readonly number[];
  /** Decay time constant of the fundamental, seconds (amplitude falls to 1/e in this time). Harmonic k decays with `decay / k ** decayExponent`. */
  readonly decay: number;
  readonly decayExponent: number;
  /** Rise time of the partials and the noise, milliseconds (under 6). */
  readonly attackMs: number;
  /** The thump: low-passed noise. */
  readonly thump: BassNoise;
  /** The finger click: band-passed noise. */
  readonly click: BassNoise;
  /** The longest a note sounds, seconds; a note is cut sooner when the next one starts. */
  readonly maxSeconds: number;
  /** The release that ends a note, milliseconds (a raised cosine to silence over the last of the note's seconds). */
  readonly releaseMs: number;
  /** What a note is scaled to before the soft clip, against its own peak. About 1 is a light grit; less is cleaner. */
  readonly drive: number;
  /** Corner of the low-pass after the soft clip, Hz. */
  readonly lowpass: number;
  /** Peak of a note after the low-pass. Under 0.9. */
  readonly peak: number;
  /** Loudness of the bass in the lab mix (the bass's `levels` default). */
  readonly level: number;
  /** The chords of the lab bass: roots in semitones above C, one chord per `countsPerChord` counts. */
  readonly progression: readonly number[];
  /** The two-chord vamp a loop of an odd number of phrases plays, for the same reason. */
  readonly vamp: readonly number[];
  readonly countsPerChord: number;
  /** The fifth, in semitones above the root. */
  readonly fifth: number;
  /** The lowest note, as a MIDI number (36 is C2, 65.4 Hz). Every note is folded into the octave from here up (C2 to just under C3). */
  readonly lowest: number;
}

/** The numbers to tune by ear. */
export const BASS: BassSpec = {
  label: 'Bass',
  harmonics: [1, 2, 3, 4, 5],
  gains: [1, 0.6, 0.35, 0.2, 0.1],
  decay: 0.3,
  decayExponent: 0.7,
  attackMs: 3,
  thump: { ms: 6, band: [0, 500], tau: 0.004, db: -10 },
  click: { ms: 8, band: [700, 1500], tau: 0.004, db: -22 },
  maxSeconds: 0.4,
  releaseMs: 30,
  drive: 0.7,
  lowpass: 1500,
  peak: 0.85,
  level: 0.7,
  progression: [0, 5, 7, 5], // C F G F
  vamp: [0, 7], // C G
  countsPerChord: 4,
  fifth: 7,
  lowest: 36,
};

/** Note names, for the interface and the tests. */
const NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const;

/** Frequency of a MIDI note number (A4 = 69 = 440 Hz). */
export const midiHz = (midi: number): number => 440 * 2 ** ((midi - 69) / 12);

/** Name of a MIDI note number: 36 is "C2". */
export const midiName = (midi: number): string => `${NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;

/** The MIDI note of a pitch class (semitones above C, any integer) in the octave from `BASS.lowest` up. */
const fold = (semitones: number): number => BASS.lowest + ((((semitones - BASS.lowest) % 12) + 12) % 12);

/** One note of the line. */
export interface BassNote {
  /** Phrase of the loop, from 0, and the count in it (2.5 is "2&"). */
  readonly phrase: number;
  readonly count: number;
  /** Beats from the start of the loop (count 1 of the first phrase is 0): `phrase * 8 + count - 1`. */
  readonly beat: number;
  /** Pitch: MIDI number and Hz. */
  readonly midi: number;
  readonly hz: number;
  /** The chord this note belongs to (the fifth of it) or anticipates (the root of the next one), as the root's semitones above C. */
  readonly chord: number;
  /** `fifth` on the "&", `next-root` on a whole count. */
  readonly role: 'fifth' | 'next-root';
}

/** The chords of a loop of `phrases` phrases, one per `countsPerChord` counts, for one cycle: the I-IV-V-IV progression when the loop holds whole cycles of two phrases, the vamp when it does not. */
export function bassChords(phrases: number): readonly number[] {
  return phrases % 2 === 0 ? BASS.progression : BASS.vamp;
}

/**
 * The bass line of a lab loop: a note on each of the counts in `counts` (the lab's 2&, 4, 6& and 8), in every phrase, in order. On a "&" it is the fifth of the chord
 * being played; on a whole count it is the root of the next chord (the chord after the last one is the first: the line joins).
 */
export function bassLine(phrases: number, counts: readonly number[]): readonly BassNote[] {
  const chords = bassChords(phrases);
  const notes: BassNote[] = [];
  for (let phrase = 0; phrase < phrases; phrase++) {
    for (const count of counts) {
      const beat = phrase * PHRASE_BEATS + count - 1;
      const index = Math.floor(beat / BASS.countsPerChord);
      const anticipate = Number.isInteger(count);
      const chord = chords[(index + (anticipate ? 1 : 0)) % chords.length]!;
      const midi = fold(anticipate ? chord : chord + BASS.fifth);
      notes.push({ phrase, count, beat, midi, hz: midiHz(midi), chord, role: anticipate ? 'next-root' : 'fifth' });
    }
  }
  return notes;
}

/** A noise burst: hash noise, band-passed, set to unit RMS, then shaped by the attack, the fall `tau`, the fade-out over its last quarter, and `level`. */
function noiseBurst(spec: BassNoise, seed: number, sampleRate: number, attackSamples: number, level: number): Float64Array {
  const n = Math.max(1, Math.round((spec.ms / 1000) * sampleRate));
  const x = new Float64Array(n);
  for (let k = 0; k < n; k++) x[k] = hashNoise(k + seed);
  if (spec.band[0] > 0) butterworth(x, 'high', spec.band[0], sampleRate);
  if (spec.band[1] < 0.45 * sampleRate) butterworth(x, 'low', spec.band[1], sampleRate);
  let sum = 0;
  for (let k = 0; k < n; k++) sum += x[k]! ** 2;
  const norm = sum > 0 ? (level * 10 ** (spec.db / 20)) / Math.sqrt(sum / n) : 0;
  const fade0 = Math.floor(0.75 * n);
  for (let k = 0; k < n; k++) {
    const fade = k < fade0 ? 1 : 0.5 * (1 + Math.cos((Math.PI * (k - fade0)) / (n - fade0)));
    x[k] = x[k]! * norm * Math.exp(-k / (spec.tau * sampleRate)) * Math.min(1, (k + 1) / attackSamples) * fade;
  }
  return x;
}

/** Seeds that keep the thump's noise and the click's apart. */
const SEEDS = { thump: 0, click: 100003 } as const;

/**
 * One note of f0 `hz`, `seconds` long. With `damped` (the default) it ends in a release (BASS.releaseMs, a raised cosine to exactly zero at the last sample), so what
 * follows it starts from silence; undamped it rings on to the end of the buffer, for measuring the decay. Peak `BASS.peak`. Pure.
 */
export function bassNote(hz: number, sampleRate: number, seconds: number = BASS.maxSeconds, damped = true): Float32Array {
  const n = Math.max(1, Math.round(seconds * sampleRate));
  const out = new Float64Array(n);
  const attack = Math.max(1, Math.round((BASS.attackMs / 1000) * sampleRate));
  const ceiling = 0.45 * sampleRate; // a partial above this would alias

  BASS.harmonics.forEach((k, i) => {
    if (hz * k > ceiling) return;
    const w = (2 * Math.PI * hz * k) / sampleRate;
    const decay = Math.exp(-1 / ((BASS.decay / k ** BASS.decayExponent) * sampleRate));
    let env = BASS.gains[i]!;
    for (let j = 0; j < n; j++) {
      out[j]! += env * Math.sin(w * j) * Math.min(1, j / attack);
      env *= decay;
    }
  });

  // the pluck: the thump and the finger click, against the RMS of the partials at their start (sum of g^2 / 2)
  const reference = Math.sqrt(BASS.gains.reduce((s, g) => s + g * g, 0) / 2);
  for (const [spec, seed] of [[BASS.thump, SEEDS.thump], [BASS.click, SEEDS.click]] as const) {
    const burst = noiseBurst(spec, seed, sampleRate, attack, reference);
    for (let j = 0; j < Math.min(n, burst.length); j++) out[j]! += burst[j]!;
  }

  // a mild soft clip (scaled so the loudest sample is `drive`), then the low-pass
  let peak = 0;
  for (const v of out) peak = Math.max(peak, Math.abs(v));
  const scale = peak > 0 ? BASS.drive / peak : 0;
  for (let j = 0; j < n; j++) out[j] = Math.tanh(out[j]! * scale);
  butterworth(out, 'low', BASS.lowpass, sampleRate);

  // the damping: a raised-cosine release over the last releaseMs, to zero at the last sample
  if (damped) {
    const release = Math.min(n, Math.max(1, Math.round((BASS.releaseMs / 1000) * sampleRate)));
    for (let k = 0; k < release; k++) out[n - release + k]! *= 0.5 * (1 + Math.cos((Math.PI * (k + 1)) / release));
  }

  peak = 0;
  for (const v of out) peak = Math.max(peak, Math.abs(v));
  const gain = peak > 0 ? BASS.peak / peak : 0;
  return Float32Array.from(out, (v) => v * gain);
}
